import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchUsers, createUser, updateUser, deleteUser, fetchClients } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Shield, User as UserIcon, Pencil, X } from "lucide-react";

const EMPTY = { email: "", password: "", full_name: "", role: "client", assigned_client_ids: [] };

export default function UsersPage() {
  const { user, isNarwhal } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const usersQ = useQuery({ queryKey: ["users"], queryFn: fetchUsers, enabled: !!isNarwhal });
  const clientsQ = useQuery({ queryKey: ["clients"], queryFn: fetchClients, enabled: !!isNarwhal });

  const onCloseDialog = () => { setEditing(null); setForm(EMPTY); };

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: () => { toast.success("Usuario creado"); qc.invalidateQueries(["users"]); onCloseDialog(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo crear"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => updateUser(id, body),
    onSuccess: () => { toast.success("Usuario actualizado"); qc.invalidateQueries(["users"]); onCloseDialog(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo actualizar"),
  });
  const deleteMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => { toast.success("Usuario eliminado"); qc.invalidateQueries(["users"]); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo eliminar"),
  });

  if (!user) return null;
  if (!isNarwhal) return <Navigate to="/dashboard" replace />;

  const onSubmit = (e) => {
    e.preventDefault();
    const body = {
      email: form.email,
      full_name: form.full_name,
      role: form.role,
      assigned_client_ids: form.role === "client" ? form.assigned_client_ids : [],
    };
    if (editing) {
      const upd = { ...body };
      if (form.password) upd.password = form.password;
      updateMut.mutate({ id: editing.id, body: upd });
    } else {
      createMut.mutate({ ...body, password: form.password });
    }
  };

  const toggleClient = (cid) => setForm((f) => ({
    ...f,
    assigned_client_ids: (f.assigned_client_ids || []).includes(cid)
      ? f.assigned_client_ids.filter((x) => x !== cid)
      : [...(f.assigned_client_ids || []), cid],
  }));

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Usuarios</h1>
          <p className="text-xs text-slate-500 mt-1">Gestión de accesos · Narwhal staff y clientes externos.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setForm({ ...EMPTY, _open: true }); }} className="gap-2" data-testid="users-add-btn">
          <Plus className="h-4 w-4" /> Nuevo usuario
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Usuario</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Rol</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Clientes asignados</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(usersQ.data || []).map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2.5 font-medium text-slate-900">{u.full_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{u.email}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${
                    u.role === "narwhal" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}>
                    {u.role === "narwhal" ? <><Shield className="h-3 w-3" /> Narwhal</> : <><UserIcon className="h-3 w-3" /> Cliente</>}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-500">
                  {u.role === "narwhal" ? <span className="italic">Acceso completo</span> :
                    u.assigned_client_ids?.length ? (
                      <div className="flex flex-wrap gap-1">{u.assigned_client_ids.map((cid) => {
                        const c = (clientsQ.data || []).find((x) => x.id === cid);
                        return <span key={cid} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{c?.name || cid.slice(0, 6)}</span>;
                      })}</div>
                    ) : <span className="italic">— sin asignar —</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={() => { setEditing(u); setForm({ ...EMPTY, ...u, password: "" }); }} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`user-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  {u.id !== user.id && (
                    <button onClick={() => { if (confirm(`¿Eliminar ${u.full_name}?`)) deleteMut.mutate(u.id); }} className="text-rose-500 hover:text-rose-700 p-1 ml-1" data-testid={`user-del-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
            {(usersQ.data || []).length === 0 && !usersQ.isLoading && (
              <tr><td colSpan={5} className="text-center text-slate-400 italic py-6">Sin usuarios aún. Hacé clic en &quot;Nuevo usuario&quot;.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing || !!form._open} onOpenChange={(o) => !o && onCloseDialog()}>
        <DialogContent className="max-w-md" data-testid="users-dialog">
          <DialogHeader><DialogTitle>{editing ? `Editar ${editing.full_name}` : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-3 mt-2">
            <div>
              <Label className="text-[11px] text-slate-600">Nombre completo</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="h-9 text-sm mt-1" data-testid="users-name" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-600">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editing} className="h-9 text-sm mt-1" data-testid="users-email" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-600">{editing ? "Nueva contraseña (vacío = mantener actual)" : "Contraseña"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} className="h-9 text-sm mt-1" data-testid="users-password" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-600">Rol</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-9 text-sm mt-1 border border-slate-200 rounded-md px-2 bg-white" data-testid="users-role">
                <option value="narwhal">Narwhal staff (acceso completo)</option>
                <option value="client">Cliente (acceso restringido)</option>
              </select>
            </div>
            {form.role === "client" && (
              <div>
                <Label className="text-[11px] text-slate-600">Clientes a los que tiene acceso</Label>
                <div className="mt-1 border border-slate-200 rounded-md max-h-44 overflow-y-auto divide-y divide-slate-100">
                  {(clientsQ.data || []).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={form.assigned_client_ids?.includes(c.id)} onChange={() => toggleClient(c.id)} data-testid={`users-client-${c.id}`} />
                      <span className="flex-1">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onCloseDialog}>Cancelar</Button>
              <Button type="submit" disabled={createMut.isPending || updateMut.isPending} data-testid="users-save">
                {editing ? "Guardar cambios" : "Crear usuario"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
